import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Contact, Loader2, Check, ExternalLink, MapPin, Phone, Mail, Globe, Sparkles, ShieldCheck, Info } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { PostalCodeInput } from "../components/PostalCodeInput";
import { AddressInput } from "../components/AddressInput";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

const EMPTY = { address: "", postal_code: "", city: "", phone: "", email: "", website: "" };

/** Offentlig kontaktinfo for arrangøren – vises på arrangørsiden og arrangementssidene (krav fra Vipps MobilePay der salget skjer). */
export default function OrganizerContactPage() {
  const { t } = useLang();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/my/contact")
      .then((res) => {
        setData(res.data);
        setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(res.data.contact).filter(([k]) => k in EMPTY)) });
      })
      .catch(() => setError(true));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const suggestions = data?.suggestions || {};
  const applySuggestion = (keys) => {
    setForm((f) => {
      const next = { ...f };
      keys.forEach((k) => { if (suggestions[k]) next[k] = suggestions[k]; });
      return next;
    });
  };
  const hasAddressSuggestion = !!(suggestions.address || suggestions.postal_code || suggestions.city);
  const complete = !!(form.address.trim() && form.postal_code.trim() && form.city.trim() && form.phone.trim() && form.email.trim());

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/my/contact", form);
      setData((d) => ({ ...d, contact: res.data.contact }));
      setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(res.data.contact).filter(([k]) => k in EMPTY)) });
      toast.success(t("Kontaktinfo lagret – vises nå på arrangørsiden din"));
      refresh?.();
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke lagre")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2" data-testid="contact-title">
            <Contact className="w-7 h-7 text-brand" /> {t("Kontaktinfo (offentlig)")}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t("Dette vises på arrangørsiden og under hvert arrangement, sammen med organisasjonsnavn og org.nr. Ingenting publiseres før du lagrer.")}
          </p>
        </div>

        <div className="rounded-xl border border-border bg-accent/60 px-4 py-3 text-sm flex gap-3" data-testid="contact-vipps-info">
          <ShieldCheck className="w-5 h-5 shrink-0 text-brand mt-0.5" />
          <p className="min-w-0 break-words">
            <span className="font-medium">{t("Hvorfor?")}</span>{" "}
            {t("Vipps MobilePay krever at organisasjonsnavn, org.nr., adresse, telefon og e-post er godt synlig der billettene selges. Når salget skjer via Right in, gjelder kravet denne siden – og i Vipps-bestillingen oppgir du arrangørsiden din som salgssted.")}
            {data?.public_url && (
              <>
                {" "}
                <a href={data.public_url} target="_blank" rel="noreferrer" className="underline underline-offset-4 inline-flex items-center gap-1 break-all" data-testid="contact-public-url">
                  {data.public_url.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" />
                </a>
              </>
            )}
          </p>
        </div>

        {error ? (
          <Card className="border-border"><CardContent className="p-6 text-sm text-muted-foreground" data-testid="contact-error">{t("Kunne ikke hente kontaktinfo – last siden på nytt.")}</CardContent></Card>
        ) : data === null ? (
          <Card className="border-border"><CardContent className="p-6 space-y-3" data-testid="contact-loading"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-2/3" /></CardContent></Card>
        ) : (
          <Card className="border-border">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm">
                  <span className="font-medium">{data.org_name}</span>
                  {data.org_number && <span className="text-muted-foreground"> · {t("Org.nr {n}", { n: data.org_number })}</span>}
                </div>
                {complete ? (
                  <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" data-testid="contact-status-badge">{t("Komplett")}</Badge>
                ) : (
                  <Badge variant="outline" className="border-[hsl(var(--warning))] text-[hsl(var(--warning))]" data-testid="contact-status-badge">{t("Mangler felt")}</Badge>
                )}
              </div>

              {hasAddressSuggestion && !form.address && (
                <button
                  type="button"
                  onClick={() => applySuggestion(["address", "postal_code", "city", "website"])}
                  className="w-full text-left rounded-lg border border-dashed border-brand/50 bg-brand-soft/40 px-3 py-2.5 text-sm flex items-start gap-2 hover:bg-brand-soft transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="contact-use-brreg-address"
                >
                  <Sparkles className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium">{t("Bruk adressen fra Brønnøysundregistrene:")}</span>{" "}
                    {[suggestions.address, [suggestions.postal_code, suggestions.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
                  </span>
                </button>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                <div className="sm:col-span-6 space-y-1.5">
                  <Label htmlFor="contact-address" className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {t("Adresse")}</Label>
                  <AddressInput id="contact-address" value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v }))} onSelect={(a) => setForm((f) => ({ ...f, address: a.adressetekst, postal_code: a.postnummer || f.postal_code, city: a.poststed || f.city }))} postalCode={form.postal_code} placeholder={t("Gateadresse / postboks")} testId="contact-address-input" />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="contact-postal">{t("Postnummer")}</Label>
                  <PostalCodeInput id="contact-postal" value={form.postal_code} onChange={(v) => setForm((f) => ({ ...f, postal_code: v }))} onPlace={(p) => setForm((f) => ({ ...f, city: p.poststed }))} placeholder="5460" testId="contact-postal-input" />
                </div>
                <div className="sm:col-span-4 space-y-1.5">
                  <Label htmlFor="contact-city">{t("Poststed")}</Label>
                  <Input id="contact-city" value={form.city} onChange={set("city")} placeholder="Husnes" data-testid="contact-city-input" />
                </div>
                <div className="sm:col-span-3 space-y-1.5">
                  <Label htmlFor="contact-phone" className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {t("Telefon")}</Label>
                  <Input id="contact-phone" value={form.phone} onChange={set("phone")} inputMode="tel" placeholder="900 00 000" data-testid="contact-phone-input" />
                  {suggestions.phone && !form.phone && (
                    <button type="button" className="text-xs text-brand underline underline-offset-4" onClick={() => applySuggestion(["phone"])} data-testid="contact-use-phone">{t("Bruk {v}", { v: suggestions.phone })}</button>
                  )}
                </div>
                <div className="sm:col-span-3 space-y-1.5">
                  <Label htmlFor="contact-email" className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {t("E-post")}</Label>
                  <Input id="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="post@organisasjon.no" data-testid="contact-email-input" />
                  {suggestions.email && !form.email && (
                    <button type="button" className="text-xs text-brand underline underline-offset-4" onClick={() => applySuggestion(["email"])} data-testid="contact-use-email">{t("Bruk {v}", { v: suggestions.email })}</button>
                  )}
                </div>
                <div className="sm:col-span-6 space-y-1.5">
                  <Label htmlFor="contact-website" className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> {t("Nettside (valgfritt)")}</Label>
                  <Input id="contact-website" value={form.website} onChange={set("website")} placeholder="www.organisasjon.no" data-testid="contact-website-input" />
                </div>
              </div>

              <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                {t("Bruk organisasjonens felles adresse, telefon og e-post – ikke private opplysninger. Alt her blir synlig for publikum.")}
              </p>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Button asChild variant="ghost" size="sm" data-testid="contact-terms-link">
                  <Link to="/vilkar">{t("Se salgsvilkårene")}</Link>
                </Button>
                <Button onClick={save} disabled={saving} data-testid="contact-save-button">
                  {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />} {t("Lagre og publiser")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
